import React, { useState, useEffect } from 'react';
import {
  Users,
  TrendingUp,
  DollarSign,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Bell,
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { adminService } from '../services/adminService';
import ApiTest from '../components/ApiTest';

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch analytics data on component mount
  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAnalytics();
      setAnalytics(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Real analytics data from backend
  const realStats = analytics ? [
    {
      title: 'Total Users',
      value: analytics.totalUsers?.toLocaleString() || '0',
      change: analytics.newUsersLast7Days ? `+${analytics.newUsersLast7Days}` : '+0',
      changeType: 'positive',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Total Revenue',
      value: analytics.totalRevenue?.toLocaleString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Total Coins',
      value: analytics.totalCoins?.toLocaleString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'from-yellow-500 to-yellow-600',
    },
    {
      title: 'Total Transactions',
      value: analytics.totalTransactions?.toLocaleString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: Activity,
      color: 'from-pink-500 to-pink-600',
    },
    {
      title: 'Total Collaborations',
      value: analytics.totalCollaborations?.toLocaleString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'from-teal-500 to-teal-600',
    },
    {
      title: 'Active Campaigns',
      value: analytics.totalCampaigns?.toString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'Total Applications',
      value: analytics.totalApplications?.toString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: Calendar,
      color: 'from-orange-500 to-orange-600',
    },
    {
      title: 'Active Users (24h)',
      value: analytics.activeUsers?.['24h']?.toLocaleString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: Clock,
      color: 'from-gray-500 to-gray-600',
    },
    {
      title: 'Active Users (7d)',
      value: analytics.activeUsers?.['7d']?.toLocaleString() || '0',
      change: '+0%',
      changeType: 'positive',
      icon: Clock,
      color: 'from-gray-700 to-gray-800',
    },
    {
      title: 'Retention Rate (7d)',
      value: analytics.retentionRate?.['7d'] + '%' || '0%',
      change: '+0%',
      changeType: 'positive',
      icon: ArrowUpRight,
      color: 'from-green-400 to-green-500',
    },
    {
      title: 'Retention Rate (30d)',
      value: analytics.retentionRate?.['30d'] + '%' || '0%',
      change: '+0%',
      changeType: 'positive',
      icon: ArrowUpRight,
      color: 'from-green-700 to-green-800',
    },
  ] : [];

  // Use real data if available, otherwise fallback to mock
  const displayStats = realStats.length > 0 ? realStats : [
    {
      title: 'Total Users',
      value: '12,847',
      change: '+12.5%',
      changeType: 'positive',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Total Revenue',
      value: '2,847,392',
      change: '+8.2%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Active Campaigns',
      value: '156',
      change: '+23.1%',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'Total Applications',
      value: '3,847',
      change: '+18.7%',
      changeType: 'positive',
      icon: Activity,
      color: 'from-orange-500 to-orange-600',
    },
  ];

  // Mock data for charts
  const chartData = [
    { name: 'Jan', users: 4000, campaigns: 2400, applications: 2400 },
    { name: 'Feb', users: 3000, campaigns: 1398, applications: 2210 },
    { name: 'Mar', users: 2000, campaigns: 9800, applications: 2290 },
    { name: 'Apr', users: 2780, campaigns: 3908, applications: 2000 },
    { name: 'May', users: 1890, campaigns: 4800, applications: 2181 },
    { name: 'Jun', users: 2390, campaigns: 3800, applications: 2500 },
    { name: 'Jul', users: 3490, campaigns: 4300, applications: 2100 },
  ];

  // Mock data for recent activity
  const recentActivity = [
    {
      id: 1,
      type: 'user_registered',
      message: 'New user John Doe registered',
      time: '5 minutes ago',
      status: 'success',
    },
    {
      id: 2,
      type: 'campaign_created',
      message: 'Summer Sale campaign created',
      time: '15 minutes ago',
      status: 'info',
    },
    {
      id: 3,
      type: 'payment_received',
      message: 'Payment received from user #1234',
      time: '1 hour ago',
      status: 'success',
    },
    {
      id: 4,
      type: 'support_ticket',
      message: 'New support ticket #5678 opened',
      time: '2 hours ago',
      status: 'warning',
    },
  ];

  // Real chart data from backend analytics
  const realChartData = analytics && analytics.months && Array.isArray(analytics.months)
  ? analytics.months.map((month, index) => ({
      name: month,
      users: analytics.userGrowth?.[index] || 0,
      revenue: analytics.revenueSummary?.[index] || 0, // Use revenueSummary from backend
      campaigns: analytics.topCampaigns?.[index]?.applicationCount || 0,
    }))
  : [];

  // Use real data if available, otherwise fallback to mock
  const displayChartData = realChartData.length > 0 ? realChartData : [
    { name: 'Jan', users: 4000, revenue: 2400, campaigns: 2400 },
    { name: 'Feb', users: 3000, revenue: 1398, campaigns: 2210 },
    { name: 'Mar', users: 2000, revenue: 9800, campaigns: 2290 },
    { name: 'Apr', users: 2780, revenue: 3908, campaigns: 2000 },
    { name: 'May', users: 1890, revenue: 4800, campaigns: 2181 },
    { name: 'Jun', users: 2390, revenue: 3800, campaigns: 2500 },
    { name: 'Jul', users: 3490, revenue: 4300, campaigns: 2100 },
  ];

  // Real role distribution data from backend
  const realPieData = analytics && analytics.roleCounts && Array.isArray(analytics.roleCounts) ? analytics.roleCounts.map((role, index) => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];
    return {
      name: role.role,
      value: role.count,
      color: colors[index % colors.length]
    };
  }) : [];

  // Use real data if available, otherwise fallback to mock
  const displayPieData = realPieData.length > 0 ? realPieData : [
    { name: 'Brand', value: 400, color: '#3B82F6' },
    { name: 'Influencer', value: 300, color: '#10B981' },
    { name: 'SMA', value: 200, color: '#F59E0B' },
    { name: 'Admin', value: 100, color: '#8B5CF6' },
  ];

  // Real recent activity data from backend
  const realRecentActivity = analytics ? [
    ...(analytics.recentUsers && Array.isArray(analytics.recentUsers) ? analytics.recentUsers.slice(0, 3).map(user => ({
      id: user.id,
      type: 'user_registered',
      message: `New user ${user.name || 'Unknown'} registered`,
      time: user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Recently',
      status: 'success',
    })) : []),
    ...(analytics.recentTransactions && Array.isArray(analytics.recentTransactions) ? analytics.recentTransactions.slice(0, 2).map(tx => ({
      id: tx.id,
      type: 'coin_transaction',
      message: `Coin transaction: ${tx.amount} coins`,
      time: tx.created_at ? new Date(tx.created_at).toLocaleDateString() : 'Recently',
      status: 'info',
    })) : [])
  ] : [];

  // Use real data if available, otherwise fallback to mock
  const displayRecentActivity = realRecentActivity.length > 0 ? realRecentActivity : [
    {
      id: 1,
      type: 'user_registered',
      message: 'New user John Doe registered',
      time: '2 minutes ago',
      status: 'success',
    },
    {
      id: 2,
      type: 'campaign_created',
      message: 'Summer Sale campaign created',
      time: '15 minutes ago',
      status: 'info',
    },
    {
      id: 3,
      type: 'payment_received',
      message: 'Payment received from user #1234',
      time: '1 hour ago',
      status: 'success',
    },
    {
      id: 4,
      type: 'support_ticket',
      message: 'New support ticket #5678 opened',
      time: '2 hours ago',
      status: 'warning',
    },
  ];

  const StatCard = ({ title, value, change, changeType, icon: Icon, color }) => (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-full bg-gradient-to-r ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
      <div className="mt-4 flex items-center">
        {changeType === 'positive' ? (
          <ArrowUpRight className="h-4 w-4 text-green-500" />
        ) : (
          <ArrowDownRight className="h-4 w-4 text-red-500" />
        )}
        <span
          className={`ml-1 text-sm font-medium ${
            changeType === 'positive' ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {change}
        </span>
        <span className="ml-2 text-sm text-gray-500">from last month</span>
      </div>
    </div>
  );

  const ActivityItem = ({ type, message, time, status }) => {
    const getStatusIcon = () => {
      switch (status) {
        case 'success':
          return <CheckCircle className="h-4 w-4 text-green-500" />;
        case 'warning':
          return <AlertCircle className="h-4 w-4 text-yellow-500" />;
        case 'info':
          return <Clock className="h-4 w-4 text-blue-500" />;
        default:
          return <Clock className="h-4 w-4 text-gray-500" />;
      }
    };

    return (
      <div className="flex items-start space-x-3 py-3">
        <div className="flex-shrink-0">{getStatusIcon()}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900">{message}</p>
          <p className="text-sm text-gray-500">{time}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  // Add error boundary for chart rendering
  const renderChart = (chartType, data) => {
    try {
      if (!data || data.length === 0) {
        return (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No chart data available
          </div>
        );
      }

      switch (chartType) {
        case 'line':
          return (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="users" stroke="#3B82F6" strokeWidth={2} />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} />
            </LineChart>
          );
        case 'bar':
          return (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="users" fill="#3B82F6" />
              <Bar dataKey="revenue" fill="#10B981" />
            </BarChart>
          );
        case 'pie':
          return (
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          );
        default:
          return <div>Invalid chart type</div>;
      }
    } catch (error) {
      console.error('Chart rendering error:', error);
      return (
        <div className="flex items-center justify-center h-64 text-red-500">
          Error rendering chart: {error.message}
        </div>
      );
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="card p-12 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={fetchAnalytics} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            {error ? 'Showing demo data (API connection failed)' : 'Welcome to your admin dashboard'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="input-field"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button onClick={fetchAnalytics} className="btn-secondary">
            Refresh
          </button>
        </div>
      </div>

             {/* Stats Grid */}
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
         {displayStats.map((stat, index) => (
           <StatCard key={index} {...stat} />
         ))}
       </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users & Revenue Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Users & Revenue Trend</h3>
                     <ResponsiveContainer width="100%" height={300}>
             {renderChart('line', displayChartData)}
           </ResponsiveContainer>
        </div>

        {/* Campaign Performance */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Performance</h3>
                     <ResponsiveContainer width="100%" height={300}>
             {renderChart('bar', displayChartData)}
           </ResponsiveContainer>
        </div>
      </div>

      {/* User Distribution & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Distribution</h3>
                     <ResponsiveContainer width="100%" height={300}>
             {renderChart('pie', displayPieData)}
           </ResponsiveContainer>
        </div>

        {/* Recent Activity */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-1">
                            {displayRecentActivity.map((activity) => (
                  <ActivityItem key={activity.id} {...activity} />
                ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View all activity →
            </button>
          </div>
        </div>
      </div>

      {/* API Test Component */}
      <ApiTest />

      {/* Quick Actions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <Users className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Add User</span>
          </button>
          <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <TrendingUp className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Create Campaign</span>
          </button>
          <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <Bell className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Send Notification</span>
          </button>
          <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
            <Activity className="h-8 w-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">View Reports</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
